import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import { AllTransactionRequest, SwapTransactionResponse, TransactionRequest, TransactionResponse } from 'types'

import {
  setInvestorTransactionsData,
  setSwapTransactionsData,
  setTransactionsData,
} from 'store/reducers-common/Pool/slice'
import { getSignature } from 'utils/getSignature'

export const queryTransaction = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    getAllTransactions: builder.query<TransactionResponse, AllTransactionRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const url = `${apiPath.pool}/transactions/cashflows?limit=${arg?.limit}&page=${arg?.page}&type=Deposit`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetAllCashflows',
          url,
        })

        if (!signature) {
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        return { data: result.data as TransactionResponse }
      },
    }),
    getSwapTransaction: builder.query<SwapTransactionResponse, TransactionRequest>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setSwapTransactionsData(data))
        } catch (err) {
          throw new Error(`getSwapTransaction query error: ${err}`)
        }
      },
      providesTags: (result) =>
        result
          ? [
              // Provides a tag for each post in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ id }) => ({ id, type: 'SwapTransaction' as const })),
              { id: 'PARTIAL-LIST', type: 'SwapTransaction' },
            ]
          : [{ id: 'PARTIAL-LIST', type: 'SwapTransaction' }],
      query: (data) => `${apiPath.pool}/${data.poolAddress}/transactions/swaps?limit=${data.limit}&page=${data.page}`,
    }),
    getTransaction: builder.query<TransactionResponse, TransactionRequest>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setTransactionsData(data))
        } catch (err) {
          throw new Error(`getTransaction query error: ${err}`)
        }
      },
      providesTags: (result) =>
        result
          ? [
              // Provides a tag for each post in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ id }) => ({ id, type: 'Transaction' as const })),
              { id: 'PARTIAL-LIST', type: 'Transaction' },
            ]
          : [{ id: 'PARTIAL-LIST', type: 'Transaction' }],
      query: (data) =>
        `${apiPath.pool}/${data.poolAddress}/transactions/cashflows?limit=${data.limit}&page=${data.page}&type=Deposit&type=Withdrawal`,
    }),
    getTransactionByUser: builder.query<TransactionResponse, TransactionRequest>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setInvestorTransactionsData(data))
        } catch (err) {
          throw new Error(`getTransactionByUser query error: ${err}`)
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ id, type: 'Transaction' as const })),
              { id: 'PARTIAL-LIST', type: 'Transaction' },
            ]
          : [{ id: 'PARTIAL-LIST', type: 'Transaction' }],
      query: (data) =>
        `${apiPath.pool}/${data.poolAddress}/transactions/cashflows?limit=${data.limit}&page=${data.page}${data.investorAddress ? `&investorAddress=${data.investorAddress}` : ''}&type=Deposit&type=Withdrawal`,
    }),
  }),
  reducerPath: 'transaction',
  tagTypes: ['Transaction', 'SwapTransaction'],
})
export const {
  useGetAllTransactionsQuery,
  useGetSwapTransactionQuery,
  useGetTransactionByUserQuery,
  useGetTransactionQuery,
} = queryTransaction
